import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

export interface UseAsyncResourceOptions<T> {
  enabled?: boolean;
  deps: readonly unknown[];
  fetch: () => Promise<T>;
  formatError?: (err: unknown) => string;
  onDisabled?: () => void;
  initialLoading?: boolean;
  clearOnDisabled?: boolean;
  clearOnError?: boolean;
}

export interface UseAsyncResourceResult<T> {
  data: T | undefined;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
  reloadCount: number;
  setData: Dispatch<SetStateAction<T | undefined>>;
}

export function useAsyncResource<T>({
  enabled = true,
  deps,
  fetch,
  formatError = () => "Не удалось загрузить данные",
  onDisabled,
  initialLoading,
  clearOnDisabled = true,
  clearOnError = false,
}: UseAsyncResourceOptions<T>): UseAsyncResourceResult<T> {
  const [data, setData] = useState<T>();
  const [isLoading, setLoading] = useState(initialLoading ?? enabled);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (clearOnDisabled) {
        setData(undefined);
        setError(null);
      }
      onDisabled?.();
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await fetch();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          if (clearOnError) setData(undefined);
          setError(formatError(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // fetch/formatError intentionally omitted — callers stabilize via deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reloadKey, ...deps]);

  return {
    data,
    isLoading,
    error,
    reload,
    reloadCount: reloadKey,
    setData,
  };
}
